import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { LoginPage } from './LoginPage';
import { LoadingSpinner } from './LoadingSpinner';
import { API_CONFIG } from '../../config/api';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children
}) => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasSyncedUser, setHasSyncedUser] = useState(false);

  // Check for existing session and sync user to database
  useEffect(() => {
    const checkAuthAndSyncUser = async () => {
      try {
        // Only validate if we think user is authenticated
        if (isAuthenticated && !hasSyncedUser) {
          // Get token to validate session
          const token = await getAccessTokenSilently();
          
          // SYNC USER TO DATABASE ON LOGIN
          try {
            console.log('Syncing user to database after login...');
            const response = await fetch(API_CONFIG.endpoints.auth.syncUser, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log('User synced to database successfully:', data);
              setHasSyncedUser(true);
            } else {
              console.error('Failed to sync user to database:', response.status);
            }
          } catch (syncError) {
            console.error('Error syncing user to database:', syncError);
            // Don't block login if sync fails
          }
        }
      } catch (error: any) {
        console.error('Token validation failed:', error);
        // Only redirect on specific auth errors
        if (error.error === 'login_required' || error.error === 'consent_required') {
          localStorage.removeItem('auth0.is.authenticated');
          loginWithRedirect();
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };

    if (!isLoading) {
      checkAuthAndSyncUser();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, hasSyncedUser]);

  // Store authentication state for silent auth attempts
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('auth0.is.authenticated', 'true');
    } else {
      localStorage.removeItem('auth0.is.authenticated');
    }
  }, [isAuthenticated]);

  // Show loading spinner while Auth0 is initializing or checking auth
  if (isLoading || isCheckingAuth) {
    return <LoadingSpinner />;
  }

  // If there's an error, show login page
  if (error) {
    console.error('Auth0 error:', error);
    return <LoginPage />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // User is authenticated, render protected content
  return <>{children}</>;
};